import React from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { submittalWorkflowStepVariant } from '@/lib/status-variants'
import { Check, Clock, FolderOpen, Play, RotateCcw, SkipForward } from 'lucide-react'

const HANDLE_CLASS = 'opacity-0'

/** Progress of one plan step on a revision, as SubmittalReviewsCard derives it:
 *  no review rows yet is `planned`, rows with an undecided one is `active`, all
 *  rows decided is `done` — unless one of them carries a negative verdict, in
 *  which case the engine short-circuited the step and it is `returned`.
 *  `skipped` is the fifth case: a step with no rows that the engine has already
 *  moved past (every reviewer deactivated — STEP_SKIPPED in lifecycle.ts) or
 *  that a short-circuit stranded. It reads like `planned` in the data and is
 *  the opposite of it in meaning, so it needs its own label. */
export type WorkflowProgressState = 'done' | 'active' | 'planned' | 'returned' | 'skipped'

export type ProgressStepNodeData = {
  index: number
  name: string
  state: WorkflowProgressState
  overdue: boolean
  dueLabel: string | null
}

export type ProgressPublishNodeData = {
  folderName: string | null
}

export const STATE_ICON: Record<WorkflowProgressState, React.ReactNode> = {
  done: <Check className="mr-1 h-3 w-3" />,
  active: <Clock className="mr-1 h-3 w-3" />,
  returned: <RotateCcw className="mr-1 h-3 w-3" />,
  planned: null,
  skipped: <SkipForward className="mr-1 h-3 w-3" />,
}

export const STATE_LABEL_KEY: Record<WorkflowProgressState, string> = {
  done: 'Submittals_wf_step_done',
  active: 'Submittals_wf_step_active',
  returned: 'Submittals_wf_step_returned',
  planned: 'Submittals_wf_step_planned',
  skipped: 'Submittals_wf_step_skipped',
}

export const ProgressStartNode: React.FC<NodeProps> = () => {
  const { t } = useTranslation()

  return (
    <>
      <div className="flex h-8 w-[84px] items-center justify-center gap-1 rounded-full border bg-muted px-2 text-xs font-medium text-muted-foreground">
        <Play className="h-3 w-3" />
        {t('Submittals_wf_flow_start')}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} className={HANDLE_CLASS} />
    </>
  )
}

export const ProgressStepNode: React.FC<NodeProps> = ({ data }) => {
  const { t } = useTranslation()
  const step = data as unknown as ProgressStepNodeData
  const isOverdue = step.state === 'active' && step.overdue

  return (
    <>
      <Handle type="target" position={Position.Left} isConnectable={false} className={HANDLE_CLASS} />

      <div
        className={cn(
          'flex h-[72px] w-[184px] flex-col justify-center gap-1 rounded-lg border bg-card p-2.5 shadow-sm',
          (step.state === 'planned' || step.state === 'skipped') && 'bg-muted/40',
          step.state === 'active' && 'border-primary',
          (step.state === 'returned' || isOverdue) && 'border-destructive',
        )}
      >
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 text-xs font-semibold text-muted-foreground">{step.index + 1}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{step.name}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <Badge
            variant={isOverdue ? 'danger' : submittalWorkflowStepVariant(step.state)}
            className="h-5 shrink-0 whitespace-nowrap px-1.5 font-medium"
          >
            {isOverdue ? <Clock className="mr-1 h-3 w-3" /> : STATE_ICON[step.state]}
            {t(isOverdue ? 'Submittals_wf_overdue' : STATE_LABEL_KEY[step.state])}
          </Badge>
          {step.dueLabel && (
            <span
              className={cn(
                'min-w-0 truncate text-[11px]',
                isOverdue ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {step.dueLabel}
            </span>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} isConnectable={false} className={HANDLE_CLASS} />
    </>
  )
}

export const ProgressPublishNode: React.FC<NodeProps> = ({ data }) => {
  const { t } = useTranslation()
  const publish = data as unknown as ProgressPublishNodeData

  return (
    <>
      <Handle type="target" position={Position.Left} isConnectable={false} className={HANDLE_CLASS} />

      <div className="flex h-[52px] w-[152px] flex-col justify-center gap-0.5 rounded-lg border bg-card p-2 shadow-sm">
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-primary" />
          {t('Submittals_wf_flow_publish')}
        </div>
        <span className="truncate text-[11px] text-muted-foreground">
          {publish.folderName || t('Submittals_wf_flow_no_folder')}
        </span>
      </div>
    </>
  )
}

export const PROGRESS_NODE_TYPES = {
  start: ProgressStartNode,
  step: ProgressStepNode,
  publish: ProgressPublishNode,
}
